import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui-kit/sheet';
import { KB, KBType } from '@/modules/gpt-chats/models/kb';
import { CopyToClipboardButton } from '@/components/ui-kit/copy-to-clipboard-button';
import { parseBackendUtc } from '@/modules/gpt-chats/utils/date-utils';
import { getKBName } from '@/modules/gpt-chats/components/knowledge-base/utils';
import { formatFullDate } from '@/modules/gpt-chats/utils/date-utils';
import { useTranslation } from 'react-i18next';

interface KBDocumentInfoProps {
  kb?: KB;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="text-sm font-medium">{value}</p>
  </div>
);

export const KBDocumentInfo = ({ kb, open, onOpenChange }: KBDocumentInfoProps) => {
  const { t } = useTranslation();
  const documentId = kb?.id || '';
  const documentTitle = kb ? getKBName(kb, t) : '';

  const sheetData = {
    originalFileName: (kb?.kb_type === KBType.FILE ? kb.file?.name : documentTitle) || '-',
    id: documentId,
    source: kb?.link?.url || '-',
    createdDate: kb?.created_date,
    lastUpdateDate: kb?.last_updated_date,
    chunkSize: kb?.chunk_size,
    totalChunks: kb?.total_chunks,
    additionMethod: kb?.kb_type || t('LINK_LABEL'),
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none xl:w-[25%] p-0 border-l !top-[60px] !h-[calc(100vh-60px)]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="h-full flex flex-col">
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle>{t('DOCUMENT_INFORMATION')}</SheetTitle>
          </SheetHeader>

          <div className="p-6 space-y-6 overflow-y-auto">
            {kb?.kb_type !== KBType.LINK && (
              <InfoRow label={t('FILE_NAME')} value={sheetData.originalFileName} />
            )}
            <InfoRow label={t('ID')} value={sheetData.id} />
            {kb?.kb_type === KBType.LINK && (
              <div className="grid grid-cols-[140px_1fr] gap-2">
                <p className="text-sm text-muted-foreground">{t('SOURCE')}</p>
                <CopyToClipboardButton textToCopy={sheetData.source} isHoverable>
                  <p className="text-sm font-medium break-all flex-1">{sheetData.source}</p>
                </CopyToClipboardButton>
              </div>
            )}
            <InfoRow
              label={t('CREATED_DATE')}
              value={
                sheetData.createdDate
                  ? formatFullDate(new Date(parseBackendUtc(sheetData.createdDate)))
                  : '-'
              }
            />
            <InfoRow
              label={t('LAST_UPDATE_DATE')}
              value={
                sheetData.lastUpdateDate
                  ? formatFullDate(new Date(parseBackendUtc(sheetData.lastUpdateDate)))
                  : '-'
              }
            />
            <InfoRow label={t('CHUNK_SIZE')} value={sheetData.chunkSize?.toString() || '0'} />
            <InfoRow label={t('TOTAL_CHUNKS')} value={sheetData.totalChunks?.toString() || '0'} />
            <InfoRow label={t('ADDITION_METHOD')} value={sheetData.additionMethod} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
